/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Subscription,
  BehaviorSubject,
  Observable,
  from,
  sequenceEqual as rxjsSequenceEqual,
  lastValueFrom,
} from 'rxjs';
import { map, skip } from 'rxjs/operators';
import safeStringify from 'fast-safe-stringify';
import { fromEvent } from 'rxjs/internal/observable/fromEvent';
import type { JQueryStyleEventEmitter } from 'rxjs/internal/observable/fromEvent';

export function uuidv4(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export function randomStr(length: number): string {
  let result = '';
  const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  const charactersLength = characters.length;
  for (let i = 0; i < length; i++) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength));
  }
  return result;
}
/**
 * Returns a random integer between min (inclusive) and max (inclusive).
 * The value is no lower than min (or the next integer greater than min
 * if min isn't an integer) and no greater than max (or the next integer
 * lower than max if max isn't an integer).
 * Using Math.round() will give you a non-uniform distribution!
 */
export function getRandomInt(min: number, max: number): number {
  min = Math.ceil(min);
  max = Math.floor(max);
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

export function delay(ms: number, cancel?: BehaviorSubject<boolean>): Promise<boolean> {
  return new Promise<boolean>((resolve, _reject) => {
    if (cancel && cancel.getValue()) {
      // Has been canceled
      resolve(false);
      return;
    }
    let subscription: Subscription;
    let timeout: ReturnType<typeof setTimeout> | undefined = undefined;
    const clear = (isTimeout: boolean): void => {
      if (subscription) {
        subscription.unsubscribe();
      }
      if (timeout) {
        clearTimeout(timeout);
      }
      resolve(isTimeout);
    };
    timeout = setTimeout(() => clear(true), ms);
    if (cancel) {
      subscription = cancel.pipe(skip(1)).subscribe(() => clear(false));
    }
  });
}
export function delayX(ms: number, cancel?: BehaviorSubject<boolean>): Promise<boolean> {
  return new Promise<boolean>((resolve, _reject) => {
    if (cancel && cancel.getValue()) {
      // Has been canceled
      resolve(false);
      return;
    }
    let subscription: Subscription;
    const clear = (isTimeout: boolean): void => {
      if (subscription) {
        subscription.unsubscribe();
      }

      resolve(isTimeout);
    };
    const startTime = performance.now();
    const calcTime = (timestamp: number): void => {
      if (timestamp - startTime >= ms) {
        clear(true);
        return;
      }
      if (cancel && cancel.getValue()) {
        return;
      }
      requestAnimationFrame(calcTime);
    };
    requestAnimationFrame(calcTime);
    if (cancel) {
      subscription = cancel.pipe(skip(1)).subscribe(() => clear(false));
    }
  });
}

export function CancellationPromiser<T>(promise: Promise<T>, cancel: BehaviorSubject<boolean>): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    if (cancel.getValue()) {
      resolve(undefined as unknown as T); // avoid try catch
      //reject()
      return;
    }
    const subscription = cancel.pipe(skip(1)).subscribe(() => {
      resolve(undefined as unknown as T); // avoid try catch
      //reject()
    });
    promise
      .then(
        (iResolve) => resolve(iResolve),
        (iReject) => reject(iReject)
      )
      .finally(() => subscription.unsubscribe());
  });
}

export function TimeoutPromiserF<T>(
  promisef: (resolve: (value: T | PromiseLike<T>) => void, reject: (reason?: any) => void) => void,
  timeout: number,
  f: () => T
): Promise<T> {
  const promise = new Promise<T>(promisef);
  return TimeoutPromiser(promise, timeout, f);
}

export function TimeoutPromiser<T>(promise: Promise<T>, timeout: number, f: () => T): Promise<T> {
  const cancel = new BehaviorSubject<boolean>(false);
  const timeoutPromise = delay(timeout, cancel).then(() => f());
  promise.finally(() => cancel.next(true));
  return Promise.race([promise, timeoutPromise]);
}

export function groupByNum<T>(list: T[], num: number): T[][] {
  return list.reduce(
    (rv, x) => {
      if (rv.idx % num === 0) {
        rv.group.push([x]);
      } else {
        rv.group[rv.group.length - 1].push(x);
      }
      rv.idx++;
      return rv;
    },
    { idx: 0, group: new Array<T[]>() }
  ).group;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function inspectTrim(obj: any): string {
  if (typeof obj === 'undefined') {
    return 'undefined';
  }
  if (obj === null) {
    return 'null';
  }
  if (Array.isArray(obj)) {
    const items = obj.map((obj) => inspect(obj));
    return '[ ' + items.join(', ') + ' ]';
  }
  if (typeof obj === 'object') {
    return safeStringify(obj);
  }
  if (typeof obj === 'function') {
    return '[Function: ' + obj.name + ']';
  }
  if (typeof obj === 'boolean' || typeof obj === 'number') {
    return obj.toString();
  }
  return obj.toString();
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function inspect(obj: any): string {
  if (typeof obj === 'undefined') {
    return 'undefined';
  }
  if (obj === null) {
    return 'null';
  }
  if (Array.isArray(obj)) {
    const items = obj.map((obj) => inspect(obj));
    return '[ ' + items.join(', ') + ' ]';
  }
  if (typeof obj === 'object') {
    return safeStringify(obj);
  }
  if (typeof obj === 'function') {
    return '[Function: ' + obj.name + ']';
  }
  if (typeof obj === 'boolean' || typeof obj === 'number') {
    return obj.toString();
  }
  return "'" + obj.toString() + "'";
}

export function formatTokenCount(format: string): number {
  const formatRegExp = /%[sdj%]|%[0-9]+j/g;
  return format.match(formatRegExp)?.length ?? 0;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function safeFormat(f: any, ..._args: any[]): string {
  if (typeof f !== 'string') {
    const objects = new Array(arguments.length);
    for (let i = 0; i < arguments.length; i++) {
      // eslint-disable-next-line prefer-rest-params
      objects[i] = inspect(arguments[i]);
    }
    return objects.join(' ');
  }

  const formatRegExp = /%[sdj%]|%[0-9]+j/g;

  let i = 1;
  // eslint-disable-next-line prefer-rest-params
  const args = arguments;
  const len = args.length;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let str = String(f).replace(formatRegExp, (x): any => {
    if (x === '%%') {
      return '%';
    }
    if (i >= len) {
      return x;
    }
    const depthJsonMatch = x.match(/%([0-9]+)j/);
    if (depthJsonMatch && depthJsonMatch.length === 2) {
      const depth = parseInt(depthJsonMatch[1]);
      return safeStringify(args[i++], undefined, undefined, {
        depthLimit: depth,
        edgesLimit: undefined,
      });
    }

    switch (x) {
      case '%s':
        return String(args[i++]);
      case '%d':
        return Number(args[i++]);
      case '%j':
        try {
          return safeStringify(args[i++]);
        } catch (_) {
          return '[Circular]';
        }
      default:
        return x;
    }
  });
  for (let x = args[i]; i < len; x = args[++i]) {
    if (x === null || typeof x !== 'object') {
      str += ' ' + x;
    } else {
      str += ' ' + inspect(x);
    }
  }
  return str;
}

export function formEventToObservable<
  ReservedEvents extends {
    [event: string]: any;
  },
  T = any
>(
  target: JQueryStyleEventEmitter<any, T> | ArrayLike<JQueryStyleEventEmitter<any, T>>,
  eventName: keyof ReservedEvents & string
): Observable<ReservedEvents> {
  return fromEvent(target, eventName).pipe(
    map((data) => {
      const obj: any = {};
      obj['eventName'] = eventName;
      if (!data || Array.isArray(data)) {
        obj[eventName] = data;
      } else {
        obj[eventName] = [data];
      }

      return obj;
    })
  );
}

export function extractEventObservableData<
  ReservedEvents extends {
    [event: string]: any;
  },
  Ev extends keyof ReservedEvents
>(_data: ReservedEvents, eventName: Ev): ReservedEvents[Ev] {
  return _data[eventName];
}

export function linkEventObservableData<
  ReservedEvents extends {
    [event: string]: any;
  },
  Ev extends keyof ReservedEvents
>(_data: ReservedEvents, eventName: Ev, target: any, f: (_: NonNullable<ReservedEvents[Ev]>) => void): void {
  if (_data['eventName'] === eventName && _data[eventName]) {
    f.apply(target, _data[eventName]);
  }
}

export function JSONSafeParse<T>(
  text: string,
  reviver?: (this: any, key: string, value: any) => any
): { value?: T; error?: any } {
  try {
    return {
      value: JSON.parse(text, reviver),
    };
  } catch (ex) {
    return {
      error: ex,
    };
  }
}

export function isNotEmpty(value?: string): boolean {
  return value !== undefined && typeof value === 'string' && value.length > 0;
}

export async function sequenceEqual<T>(a: T[], b: T[], comparator?: (a: T, b: T) => boolean): Promise<boolean> {
  const observableA = from(a);
  const observableB = from(b);
  return lastValueFrom(rxjsSequenceEqual(observableA, comparator)(observableB));
}

export function arrayNonorderEqual<T>(a: T[], b: T[], comparator?: (a: T, b: T) => boolean): boolean {
  if (a.length !== b.length) {
    return false;
  }
  if (a.length === 0) {
    return true;
  }

  if (!comparator) {
    comparator = (a, b): boolean => a === b;
  }

  const c1 = a[0];
  for (let i = 0; i < a.length; i++) {
    const c2 = b[i];
    if (comparator(c1, c2)) {
      const ax = a.slice(1);
      const bx = [...b.slice(0, i), ...b.slice(i + 1)];
      return arrayNonorderEqual(ax, bx, comparator);
    }
  }
  return false;
}

export function getAvg(grades: number[]): number {
  const total = grades.reduce((acc, c) => acc + c, 0);
  return total / grades.length;
}

export function isPromise(value: any): boolean {
  return Boolean(value && typeof value.then === 'function');
}

type ValueOf<T> = T[keyof T];
export function getEnumKeyByEnumValue<R extends string | number, T extends { [key: string]: R }>(
  myEnum: T,
  enumValue: ValueOf<T>
): string {
  const keys = Object.keys(myEnum).filter((x) => myEnum[x] == enumValue);
  return keys.length > 0 ? keys[0] : '';
}

export function getEnumValueByKey<T extends Record<string, string | number>>(myEnum: T, key: string): T | undefined {
  if (Object.keys(myEnum).includes(key)) {
    return myEnum[key] as unknown as T;
  }
  return undefined;
}

export function isObjectEqual(obj1: object, obj2: object): boolean {
  if (!obj1 || !obj2) {
    return obj1 === obj2;
  }
  const obj1Keys = Object.keys(obj1);
  const obj2Keys = Object.keys(obj2);

  if (obj1Keys.length !== obj2Keys.length) {
    return false;
  }

  for (const key of obj1Keys) {
    if ((obj1 as unknown as any)[key] !== (obj2 as unknown as any)[key]) {
      return false;
    }
  }

  return true;
}

export function sortObjectByKeys(obj: Record<string, any>): Record<string, any> {
  // 1. 获取 Object 的所有键
  const keys = Object.keys(obj);

  // 2. 对键进行排序 (默认是升序排列)
  keys.sort(); // 也可以使用 keys.sort((a, b) => a.localeCompare(b)) 进行更精确的排序

  // 3. 创建一个新的 Object 并按照排序后的键填充
  const sortedObj: Record<string, any> = {};
  for (const key of keys) {
    sortedObj[key] = obj[key];
  }
  return sortedObj;
}

export function truncateDecimal(num: number, decimals: number): number {
  const factor = Math.pow(10, decimals);
  return Math.floor(num * factor) / factor;
}

// export function groupBy<T extends Record<string, any>, K extends keyof T>(
//   array: T[],
//   objGroupByKey: K
// ): Record<string, T[]> {
//   return array.reduce((result: Record<string, T[]>, currentItem) => {
//     const key = currentItem[objGroupByKey];
//     if (result[key]) {
//       result[key].push(currentItem);
//     } else {
//       result[key] = [currentItem];
//     }
//     return result;
//   }, {});
// }

export function toNumber(value: string | undefined): number {
  if (!value) {
    return 0;
  }
  const num = Number(value.replaceAll(/,/g, '').trim());
  if (isNaN(num)) {
    return 0;
  }
  return num;
}
